import RfqRequestEmpty from './RfqRequestEmpty';
import Card from './RfqCard';
import LeftSideListLayout from '../LeftSideListLayout';
import RequestListProvider from './RequestListProvider';
import LoadingPresenter from '@components/Loading';
import useDashboard from '@hooks/useDashboard';
import { CapaRfqQuotationType, ProjectStatusType } from '@generated/graphql';
import { LeftSideCommonProps } from '../ChatList/types';
import { onClickCardParams } from './types';
import { useCallback } from 'react';
import { SearchInput } from '@components/SearchInput';
import { CustomizedTypography, Stack } from '@components/Customized';
import { Divider } from '@material-ui/core';
import { projectStatusToString } from '@utils/enumToString';
import PaymentBanner from '@components/Dialog/ScrollableModal/PaymentBanner';

const MyRequestListsContainer = ({
  matches,
  route,
  onChangeVisible,
  onChangePath
}: LeftSideCommonProps) => {
  const { onUpdateSelectedQuotation } = useDashboard();

  const onClickCard = useCallback(
    (params: onClickCardParams) => {
      if (params.eventType === 'REQUEST') {
        return onChangePath(`/dashboard/${params.rfqId}`, () => {
          onChangeVisible(true);
          onUpdateSelectedQuotation({
            rfqId: params.rfqId,
            quotationType: 'REQUEST'
          });
        });
      } else {
        return onChangePath(
          `/dashboard/${params.rfqId}${params.isTraking ? '?p=1' : ''}`,
          () => {
            onChangeVisible(true);

            if (!params.quotationType) {
              // 레거시 - 캐파2.0 이전에 견저서 없이 채팅이 가능했었음
              return onUpdateSelectedQuotation({
                ...params,
                quotationId: undefined,
                quotationType: 'NO_QUOTATION'
              });
            } else if (params.quotationType === CapaRfqQuotationType.Simple) {
              // 레거시 - 캐파2.0이전에 간판 견적서
              return onUpdateSelectedQuotation({
                ...params,
                quotationType: CapaRfqQuotationType.Simple
              });
            } else {
              // 상세견적서
              return onUpdateSelectedQuotation({
                ...params,
                quotationType: CapaRfqQuotationType.Detail
              });
            }
          }
        );
      }
    },
    [onChangePath, onChangeVisible, onUpdateSelectedQuotation]
  );

  return (
    <RequestListProvider>
      {({ data, loading, callbackRef, setSearchParam, noSearchResult }) => {
        const rfqs = data?.fetchQuotationUserRfqs.data?.rfqs ?? [];

        if (!loading && rfqs.length === 0 && !noSearchResult)
          return <RfqRequestEmpty />;

        return (
          <>
            {loading && <LoadingPresenter open />}
            <PaymentBanner />
            <Stack padding={16}>
              <SearchInput
                onSubmit={({ keyword, tag }) => {
                  setSearchParam({
                    searchKeyword: keyword,
                    searchType:
                      tag === '전체' ? undefined : (tag as ProjectStatusType)
                  });
                }}
                options={Object.values(ProjectStatusType).map((value) => ({
                  label: projectStatusToString(value),
                  value
                }))}
              />
            </Stack>

            <Divider />

            <LeftSideListLayout route={route}>
              {noSearchResult && !loading && (
                <Stack
                  height="100%"
                  alignItems="center"
                  justifyContent="center"
                >
                  <CustomizedTypography color="textSecondary">
                    검색 결과가 없습니다.
                  </CustomizedTypography>
                </Stack>
              )}
              {rfqs?.map((rfq, index) => {
                const isLastItem = index === rfqs.length - 1;
                return (
                  <Card
                    data-cy={rfq.rfqId}
                    key={rfq.rfqId}
                    matches={matches}
                    rfq={rfq}
                    isLast={isLastItem}
                    onClickCard={onClickCard}
                    callbackRef={callbackRef}
                  >
                    <Card.ProjectInfo />
                    <Card.Status status={rfq?.searchProjectStatusTypeEnum} />
                    <Card.Accordion onClickCard={onClickCard} />
                  </Card>
                );
              })}
            </LeftSideListLayout>
          </>
        );
      }}
    </RequestListProvider>
  );
};

export default MyRequestListsContainer;
